/* eslint-disable import/extensions, import/no-unresolved */

import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, Autoplay } from 'swiper';
import 'swiper/css';

import ProductImageBadge from '../../ProductImageBadge';
import Video from './Video/Video';

import renderIcon from '../../../utils/renderIcon';

import { ProductFormContext } from '../../../context/product-form-context';
import CheveronDown from '../../../assets/icons/CheveronDown';
import CheveronUp from '../../../assets/icons/CheveronUp';


const ns = `product-gallery`;

const StopBtn = () => {
	return (
		<div className={`${ns}__stop-btn`}>
			<div className={`${ns}__stop-bar`} />
			<div className={`${ns}__stop-bar`} />
		</div>
	);
};

const Gallery = ({ product: { media, shopifyId } }) => {
	const [thumbsSwiper, setThumbsSwiper] = useState(null);
	const [gallerySwiper, setGallerySwiper] = useState(null);
	const [slides, setSlides] = useState([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isThumbnailLoop, setIsThumbnailLoop] = useState(false);
	const [maxThumbnails, setMaxThumbnails] = useState(5);
	const [swiperPlaying, setSwiperPlaying] = useState(true);
	const [shouldStop, setShouldStop] = useState(false);
	const [pauseGallery, setPauseGallery] = useState(false);
	const [mobilePlaying, setMobilePlaying] = useState(false);

	const { variant } = React.useContext(ProductFormContext);

	const gallerySwiperConfig = {
		slidesPerView: 1,
		loop: true,
		loopedSlides: 5,
		modules: [Navigation, Pagination, Thumbs, Autoplay],
		navigation: {
			nextEl: `.${ns}__swiper-button-next`,
		},
		thumbs: {
			swiper: thumbsSwiper,
		},
		threshold: 10,
		onSwiper: setGallerySwiper,
		onRealIndexChange: ({ activeIndex: _activeIndex }) => {
			setActiveIndex(_activeIndex);
		},
	};

	const thumbSwiperConfig = {
		modules: [Thumbs, Navigation],
		watchSlidesProgress: true,
		threshold: 5,
		spaceBetween: 10,
		direction: 'vertical',
		onSwiper: setThumbsSwiper,
	};

	/*
	 * This function returns a filtered array for the current active product variant.
	 * In Shopify, product media should have specific altText for certain parts of the PDP
	 * Images used for swatches should be set as: "Swatch: {option}" and media used for
	 * specific variants should be set with "Variant: {variant}". Both {option} and {variant}
	 * as they appear in Shopify. Media types include images and videos.
	 */
	const getFilteredMedia = useCallback(() => {
		const nonVariantMedia = []; // images not variant related or missing alt attribute
		const variantMedia = []; // images whos altText matches the variant title

		media.forEach((mediaItem) => {
			const { id, mediaContentType, image } = mediaItem;

			let altText = image?.altText || '';
			altText = altText.toLowerCase().trim();

			// Conditions to determin whitch array the media belongs to
			const isNonVariantMedia =
				(altText.indexOf('swatch') === -1 &&
					altText.indexOf('variant') === -1) ||
				altText === '';
			const isVariantMedia = altText.indexOf('variant') > -1;

			// Prepare mediaItem object
			let mediaItemToAdd;
			if (mediaContentType === 'IMAGE') {
				mediaItemToAdd = {
					mediaContentType,
					id, // Using parent id here because image does not have an id
					...image,
				};
			} else if (
				mediaContentType === 'VIDEO' ||
				mediaContentType === 'EXTERNAL_VIDEO'
			) {
				mediaItemToAdd = {
					mediaContentType,
					...mediaItem,
				};
			}

			// Push media item to proper array
			if (isNonVariantMedia) {
				nonVariantMedia.push(mediaItemToAdd);
			} else if (isVariantMedia) {
				const variantInAltText = altText.split(':')[1]?.trim() || '';
				const variantTitle = variant.title.trim().toLowerCase();

				if (variantInAltText === variantTitle) {
					variantMedia.push(mediaItemToAdd);
				}
			}
		});

		// Return combined array
		return [...variantMedia, ...nonVariantMedia];
	}, [media, variant]);

	useEffect(() => {
		const filteredImages = getFilteredMedia();

		setSlides(filteredImages);
		setIsThumbnailLoop(filteredImages?.length >= 5);

		if (filteredImages?.length < 5) {
			setMaxThumbnails(filteredImages?.length);
		}

		// Reset the sliders back to index 0
		if (
			filteredImages &&
			gallerySwiper &&
			thumbsSwiper &&
			gallerySwiper.activeIndex !== 0
		) {
			gallerySwiper.slideToLoop(0);
			thumbsSwiper.slideToLoop(0);
		}
	}, [variant, getFilteredMedia, thumbsSwiper, gallerySwiper]);


	const playSwiper = () => {
		const swiperElement = document.querySelector(`.${ns}__slider-mobile`);
		swiperElement?.swiper?.autoplay?.start();
		setSwiperPlaying(true);
	};

	const pauseSwiper = () => {
		const swiperElement = document.querySelector(`.${ns}__slider-mobile`);
		swiperElement?.swiper?.autoplay?.stop();
		setSwiperPlaying(false);
	};

	const handleSlideChange = () => {
		if (pauseGallery) {
			setShouldStop(true);
		}
	};

	const handleDesktopSlideChange = () => {
		return window.scrollTo({top: 0, behavior: 'smooth'});
	}

	useEffect(() => {
		pauseGallery ? pauseSwiper() : playSwiper();
	}, [pauseGallery]);

	if (slides?.length < 1) return null;

	const rootClassnames = classNames({
		[`${ns}`]: true,
		[`${ns}--mobile-playing`]: mobilePlaying,
	});

	return (
		<div className={`${rootClassnames}`}>
			{/* SLIDER DESKTOP */}
			<Swiper
				className={`${ns}__slider ${ns}__slider-desktop`}
				{...gallerySwiperConfig}
				navigation={{
					nextEl: `.${ns}__swiper-button-next`,
					prevEl: `.${ns}__swiper-button-prev`,
				}}
				onSlideChange={handleDesktopSlideChange}
			>
				{slides?.map(({ mediaContentType, ...slide }) => {
					return (
						<SwiperSlide key={slide.id} className={`${ns}__slide`}>
							{mediaContentType === 'IMAGE' ? (
								<div className={`${ns}__slide-image`}>
									<GatsbyImage
										alt={slide.altText}
										image={slide.gatsbyImageData}
										loading={'eager'}
									/>
								</div>
							) : (
								<div className={`${ns}__slide-video`}>
									<Video
										video={slide}
										activeIndex={activeIndex}
										setPauseGallery={setPauseGallery}
										setMobilePlaying={setMobilePlaying}
									/>
								</div>
							)}
						</SwiperSlide>
					);
				})}
			</Swiper>

			{/* SLIDER MOBILE */}
			<Swiper
				className={`${ns}__slider ${ns}__slider-mobile`}
				freeMode
				slidesPerView={1}
				loop
				loopedSlides={5}
				onSlideChange={handleSlideChange}
				modules={[Navigation, Pagination, Thumbs, Autoplay]}
				navigation={{
					nextEl: `.${ns}__swiper-button-next`,
					prevEl: `.${ns}__swiper-button-prev`,
				}}
				pagination={{ el: '.swiper-pagination-gallery', clickable: true }}
				autoplay={{
					delay: 3000,
					disableOnInteraction: false,
				}}
			>
				{slides?.map(({ mediaContentType, ...slide }) => {
					return (
						<SwiperSlide key={slide.id} className={`${ns}__slide`}>
							{mediaContentType === 'IMAGE' ? (
								<div className={`${ns}__slide-image`}>
									<GatsbyImage
										alt={slide.altText}
										image={slide.gatsbyImageData}
										loading={'eager'}
									/>
								</div>
							) : (
								<div className={`${ns}__slide-video`}>
									<Video
										video={slide}
										activeIndex={activeIndex}
										shouldStop={shouldStop}
										setShouldStop={setShouldStop}
										setPauseGallery={setPauseGallery}
										setMobilePlaying={setMobilePlaying}
									/>
								</div>
							)}
						</SwiperSlide>
					);
				})}
			</Swiper>

			{/* NAVIGATION */}
			<div className={`${ns}__navigation`}>
				<div className={`swiper-pagination-gallery`} />
				<button className={'swiper-button-next'}>
					<span
						aria-hidden
						// eslint-disable-next-line arrow-body-style
						onClick={() => {
							swiperPlaying ? pauseSwiper() : playSwiper();
						}}
					>
						{swiperPlaying ? <StopBtn /> : renderIcon('arrow-in-circle')}
					</span>
					<span className={`visually-hidden`}>Next</span>
				</button>
			</div>

			{/* THUMBS */}
			<Swiper
				className={`${ns}__thumbs`}
				{...thumbSwiperConfig}
				loop={isThumbnailLoop}
				loopedSlides={maxThumbnails}
				slidesPerView={maxThumbnails}
				navigation={{
					nextEl: `.${ns}__swiper-button-next`,
					prevEl: `.${ns}__swiper-button-prev`,
				}}
				click
			>
				{slides?.map(({ mediaContentType, ...slide }) => {
					return (
						<SwiperSlide key={slide.id} className={`${ns}__thumb-slide`}>
							{mediaContentType === 'IMAGE' ? (
								<div className={`${ns}__thumb-slide-image`}>
									<GatsbyImage
										alt={slide.altText}
										image={slide.gatsbyImageData}
										loading={'eager'}
									/>
								</div>
							) : (
								<div className={`${ns}__thumb-slide-video`}>
									<GatsbyImage
										alt={slide?.preview?.image?.altText}
										image={slide?.preview?.image?.gatsbyImageData}
										loading={'eager'}
									/>
									<span className={`${ns}__thumb-slide-icon`} aria-hidden>
										{renderIcon('arrow-in-circle')}
									</span>
								</div>
							)}
						</SwiperSlide>
					);
				})}
			</Swiper>
			{/* THUMBS ARROWS */}
			{slides.length > 5 && (
				<div className={`${ns}__container-thumbs`}>
					<div className={`${ns}__swiper-button-prev ${ns}__slide-btn`}>
						<CheveronUp />
					</div>
					<div className={`${ns}__swiper-button-next ${ns}__slide-btn`}>
						<CheveronDown />
					</div>
				</div>
			)}

			<ProductImageBadge shopifyId={shopifyId} />
		</div>
	);
};

export default Gallery;
